import React from 'react';
import { 
  Box, 
  Card, 
  CardContent, 
  Typography, 
  LinearProgress, 
  Tooltip,
  useTheme
} from '@mui/material';
import { 
  Storage as StorageIcon,
  DataUsage as DataUsageIcon,
  Person as PersonIcon,
  Code as CodeIcon,
  Public as PublicIcon
} from '@mui/icons-material';

// واجهة خصائص بطاقة الإحصائيات
interface UsageStatCardProps {
  title: string;
  value: number;
  max: number;
  unit?: string;
  icon: 'storage' | 'database' | 'auth' | 'functions' | 'hosting';
  description?: string;
}

// تنسيق حجم البيانات للعرض
const formatSize = (bytes: number): string => {
  if (bytes === 0) return '0 بايت';
  
  const k = 1024;
  const sizes = ['بايت', 'كيلوبايت', 'ميجابايت', 'جيجابايت', 'تيرابايت'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

// تنسيق الرقم للعرض
const formatNumber = (num: number): string => {
  return num.toLocaleString('ar-EG');
};

// مكون بطاقة إحصائيات الاستخدام
const UsageStatCard: React.FC<UsageStatCardProps> = ({ 
  title, 
  value, 
  max, 
  unit, 
  icon, 
  description 
}) => {
  const theme = useTheme();
  
  // حساب نسبة الاستخدام
  const percentage = Math.min(Math.round((value / max) * 100), 100);
  
  // تحديد لون شريط التقدم بناءً على النسبة
  const getProgressColor = () => {
    if (percentage < 50) return theme.palette.success.main;
    if (percentage < 80) return theme.palette.warning.main;
    return theme.palette.error.main;
  };
  
  // تنسيق القيمة حسب الوحدة
  const getFormattedValue = () => {
    if (icon === 'storage' || (icon === 'hosting' && unit === 'بايت')) {
      return formatSize(value);
    }
    return formatNumber(value) + (unit ? ` ${unit}` : '');
  };
  
  // تنسيق القيمة القصوى حسب الوحدة
  const getFormattedMax = () => {
    if (icon === 'storage' || (icon === 'hosting' && unit === 'بايت')) {
      return formatSize(max);
    }
    return formatNumber(max) + (unit ? ` ${unit}` : '');
  };
  
  // اختيار الأيقونة المناسبة
  const getIcon = () => {
    switch (icon) {
      case 'storage':
        return <StorageIcon fontSize="large" />;
      case 'database':
        return <DataUsageIcon fontSize="large" />;
      case 'auth':
        return <PersonIcon fontSize="large" />;
      case 'functions':
        return <CodeIcon fontSize="large" />;
      case 'hosting':
        return <PublicIcon fontSize="large" />;
      default:
        return <DataUsageIcon fontSize="large" />;
    }
  };
  
  return (
    <Card sx={{ 
      height: '100%', 
      display: 'flex', 
      flexDirection: 'column',
      borderRight: '4px solid',
      borderColor: getProgressColor()
    }}>
      <CardContent sx={{ flexGrow: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="h6" component="div" color="text.secondary">
            {title}
          </Typography>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            width: 40, 
            height: 40, 
            borderRadius: '50%', 
            bgcolor: `${getProgressColor()}20`,
            color: getProgressColor()
          }}>
            {getIcon()}
          </Box>
        </Box>
        
        <Typography variant="h4" component="div" fontWeight="bold" gutterBottom>
          {getFormattedValue()}
        </Typography>
        
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <Box sx={{ flexGrow: 1, mr: 1 }}>
            <Tooltip title={`${percentage}% من الحد المسموح`} arrow>
              <LinearProgress 
                variant="determinate" 
                value={percentage} 
                sx={{ 
                  height: 8, 
                  borderRadius: 4,
                  backgroundColor: theme.palette.grey[200],
                  '& .MuiLinearProgress-bar': {
                    backgroundColor: getProgressColor(),
                    borderRadius: 4,
                  }
                }} 
              />
            </Tooltip>
          </Box>
          <Typography variant="body2" color="text.secondary">
            {percentage}%
          </Typography>
        </Box>
        
        <Typography variant="body2" color="text.secondary">
          {getFormattedValue()} من أصل {getFormattedMax()}
        </Typography>
        
        {description && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {description}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
};

export default UsageStatCard;
